import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * RequestDemoModal — public lead capture form for forgedopsplans.com.
 *
 * Submits to /api/marketing/demo-requests (public, rate-limited, honeypot-protected).
 * No auth required. No customer data exposed. Submissions stored in
 * `marketing_leads` collection.
 */
export default function RequestDemoModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [estimatedUsers, setEstimatedUsers] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const close = () => {
    if (submitting) return;
    setName("");
    setCompany("");
    setEmail("");
    setPhone("");
    setCompanyType("");
    setEstimatedUsers("");
    setMessage("");
    setWebsite("");
    setDone(false);
    setError("");
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/marketing/demo-requests", {
        name, company, email, phone,
        company_type: companyType || null,
        estimated_users: estimatedUsers || null,
        message: message || null,
        website,
      });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 429) {
        msg = "Too many requests. Please try again in a few minutes.";
      } else if (Array.isArray(detail)) {
        // FastAPI 422 — surface the first user-friendly field error.
        const first = detail[0];
        const fieldPath = (first?.loc || []).slice(1).join(".");
        const fieldLabel = ({
          name: "Name", company: "Company", email: "Email",
          phone: "Phone", message: "Message", company_type: "Company type",
        })[fieldPath] || fieldPath || "Field";
        const reason = first?.msg || "is invalid";
        msg = reason.toLowerCase().includes("email")
          ? "Please enter a valid work email address."
          : `${fieldLabel}: ${reason}`;
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "Something went wrong. Please email info@forgedopshq.com.";
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(2, 6, 23, 0.78)", backdropFilter: "blur(8px)" }}
      data-testid="demo-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
    >
      <div
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl"
        style={{
          background: "linear-gradient(180deg, #111827 0%, #0a0f1c 100%)",
          border: "1px solid var(--fop-border-2)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(249,115,22,0.06) inset",
        }}
      >
        <button
          onClick={close}
          disabled={submitting}
          className="absolute top-4 right-4 text-[var(--fop-text-3)] hover:text-[var(--fop-text)] transition-colors"
          data-testid="demo-modal-close"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="px-7 sm:px-9 pt-9 pb-8">
          {done ? (
            <div className="text-center py-6" data-testid="demo-success">
              <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-4"
                   style={{ background: "rgba(34, 197, 94, 0.12)", border: "1px solid rgba(34,197,94,0.4)" }}>
                <CheckCircle2 size={28} className="text-[var(--fop-success)]" />
              </div>
              <h2 id="demo-modal-title" className="font-display text-2xl text-[var(--fop-text)] mb-2">
                Request received.
              </h2>
              <p className="text-[var(--fop-text-2)] text-sm mb-7 max-w-sm mx-auto">
                A ForgedOps operator will reach out within one business day to schedule
                your demo and discuss Alpha access for your team.
              </p>
              <button onClick={close} className="btn btn-primary px-8" data-testid="demo-done">
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-3">
                Request Access
              </div>
              <h2 id="demo-modal-title" className="font-display text-3xl text-[var(--fop-text)] leading-tight mb-2">
                Schedule a ForgedOps demo
              </h2>
              <p className="text-[var(--fop-text-2)] text-sm mb-6">
                Tell us about your team and we&apos;ll show you the operational plan
                room built for heavy civil and infrastructure work.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="demo-form">
                {/* Honeypot — hidden from real users. */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" required>
                    <input
                      type="text" required value={name} onChange={(e) => setName(e.target.value)}
                      data-testid="demo-name" placeholder="Your full name"
                    />
                  </Field>
                  <Field label="Company" required>
                    <input
                      type="text" required value={company} onChange={(e) => setCompany(e.target.value)}
                      data-testid="demo-company" placeholder="Contractor / Agency"
                    />
                  </Field>
                  <Field label="Work email" required>
                    <input
                      type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      data-testid="demo-email" placeholder="you@company.com"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      data-testid="demo-phone" placeholder="(optional)"
                    />
                  </Field>
                  <Field label="Company type">
                    <select
                      value={companyType} onChange={(e) => setCompanyType(e.target.value)}
                      data-testid="demo-company-type"
                    >
                      <option value="">Select…</option>
                      <option value="heavy_civil">Heavy Civil</option>
                      <option value="dot_highway">DOT / Highway</option>
                      <option value="airport_faa">Airport / FAA</option>
                      <option value="utilities">Utilities</option>
                      <option value="site_development">Site Development</option>
                      <option value="drainage">Drainage</option>
                      <option value="municipal">Municipal Infrastructure</option>
                      <option value="industrial">Industrial Construction</option>
                      <option value="engineering">Engineering / Design</option>
                      <option value="owner">Owner / Agency</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Estimated users">
                    <select
                      value={estimatedUsers} onChange={(e) => setEstimatedUsers(e.target.value)}
                      data-testid="demo-users"
                    >
                      <option value="">Select…</option>
                      <option value="1-10">1 – 10</option>
                      <option value="10-50">10 – 50</option>
                      <option value="50-200">50 – 200</option>
                      <option value="200-1000">200 – 1,000</option>
                      <option value="1000+">1,000+</option>
                    </select>
                  </Field>
                </div>

                <Field label="What are you trying to solve?">
                  <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="demo-message"
                    placeholder="Plan room pain, revision misses, utility coordination, field workflows…"
                    style={{ minHeight: "92px", resize: "vertical" }}
                  />
                </Field>

                {error && (
                  <div
                    className="flex items-start gap-2 text-sm font-mono px-3 py-2.5 rounded border"
                    style={{
                      borderColor: "rgba(220,38,38,0.35)",
                      background: "rgba(220,38,38,0.08)",
                      color: "var(--fop-danger)",
                    }}
                    data-testid="demo-error"
                  >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary flex-1"
                    style={{ minHeight: "48px", fontSize: "16px" }}
                    data-testid="demo-submit"
                  >
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Request Demo"}
                  </button>
                  <button
                    type="button"
                    onClick={close}
                    disabled={submitting}
                    className="btn btn-ghost sm:w-32"
                    style={{ minHeight: "48px" }}
                    data-testid="demo-cancel"
                  >
                    Cancel
                  </button>
                </div>

                <p className="text-[11px] font-mono text-[var(--fop-text-4)] uppercase tracking-[0.18em] pt-2">
                  We never share contact details. Used only for ForgedOps follow-up.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-1.5">
        {label}
        {required && <span className="text-[var(--fop-primary)] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
